import React, { useState, useEffect, useRef } from 'react';
import { Box, Grid, Typography, Paper, useTheme, Zoom, Stack, Button, Fade, Chip, Dialog, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { detectCurrency, detectCurrencySync } from '../utils/currencyDetection';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CodeIcon from '@mui/icons-material/Code';
import ChatIcon from '@mui/icons-material/Chat';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import MonitorIcon from '@mui/icons-material/Monitor';
import ForumIcon from '@mui/icons-material/Forum';
import LayersIcon from '@mui/icons-material/Layers';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import WebAssetIcon from '@mui/icons-material/WebAsset';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import MemoryIcon from '@mui/icons-material/Memory';
import StorageIcon from '@mui/icons-material/Storage';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ExtensionIcon from '@mui/icons-material/Extension';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Footer from './Footer';
import ChatDemo from './ChatDemo';
import AboutUs from './AboutUs';
import Contact from './Contact';
import AOS from 'aos';
import 'aos/dist/aos.css';

const features = [
  {
    icon: <ChatIcon sx={{ fontSize: 48 }} />,
    title: 'Natural Language Understanding',
    desc: 'Describe your ideas in plain English—Bixbite AI understands and translates them into actionable software requirements.',
    color: '#00796b',
  },
  {
    icon: <CodeIcon sx={{ fontSize: 48 }} />,
    title: 'Automatic Code Generation',
    desc: 'Our AI instantly generates production-ready code, so you can focus on your vision, not syntax.',
    color: '#004d40',
  },
  {
    icon: <AutoAwesomeIcon sx={{ fontSize: 48 }} />,
    title: 'Effortless Software Creation',
    desc: 'Build custom applications without writing a single line of code. Just chat, review, and launch!',
    color: '#00897b',
  },
];

const products = [
  {
    icon: <WebAssetIcon />,
    title: 'Web Applications',
    desc: 'Build modern, responsive web apps in minutes. Just describe your idea and Bixbite generates the code instantly.',
    gradient: 'linear-gradient(135deg, #00796b 0%, #4db6ac 100%)',
  },
  {
    icon: <PhoneIphoneIcon />,
    title: 'Mobile Applications',
    desc: 'Create cross-platform mobile apps effortlessly. Bixbite delivers ready-to-launch code for iOS and Android.',
    gradient: 'linear-gradient(135deg, #004d40 0%, #00796b 100%)',
  },
  {
    icon: <MemoryIcon />,
    title: 'RAG-based Apps',
    desc: 'Rapidly build Retrieval-Augmented Generation (RAG) apps for advanced AI-powered solutions with just a chat.',
    gradient: 'linear-gradient(135deg, #00897b 0%, #b2dfdb 100%)',
  },
  {
    icon: <DataObjectIcon />,
    title: 'ML Apps',
    desc: 'Deploy machine learning applications with ease. Bixbite handles the code, you focus on the model and data.',
    gradient: 'linear-gradient(135deg, #0288d1 0%, #4fc3f7 100%)',
  },
  {
    icon: <StorageIcon />,
    title: 'Data Apps',
    desc: 'Design dashboards, analytics, and data-driven tools in minutes. Bixbite automates the heavy lifting.',
    gradient: 'linear-gradient(135deg, #f9a825 0%, #ffd54f 100%)',
  },
  {
    icon: <ChatBubbleOutlineIcon />,
    title: 'Chat Bots',
    desc: 'Launch intelligent chatbots for your website or business. Bixbite makes conversational AI accessible to all.',
    gradient: 'linear-gradient(135deg, #c62828 0%, #ef5350 100%)',
  },
  {
    icon: <CodeIcon />,
    title: 'Local Copilot (LoCoPilot)',
    desc: 'Install LoCoPilot as a VS Code extension that works locally on any system with open source models. Get AI-powered code assistance without relying on cloud services.',
    gradient: 'linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%)',
  },
  {
    icon: <ExtensionIcon />,
    title: '...and More',
    desc: 'From automation tools to custom integrations, Bixbite empowers you to build any app you can imagine—fast.',
    gradient: 'linear-gradient(135deg, #004d40 0%, #00796b 100%)',
  },
];

const howItWorks = [
  {
    icon: <LightbulbIcon />,
    title: 'User describes the idea',
    desc: 'Share your software idea in plain language with Bixbite AI.',
    color: '#f9a825',
  },
  {
    icon: <SmartToyIcon />,
    title: 'Bixbite AI understands and builds the software',
    desc: 'Our AI agent interprets your requirements and starts building your application.',
    color: '#004d40',
  },
  {
    icon: <MonitorIcon />,
    title: 'Live preview is generated',
    desc: 'See a real-time preview of your application as it takes shape.',
    color: '#00796b',
  },
  {
    icon: <ForumIcon />,
    title: 'User refines through conversation',
    desc: 'Iterate and refine your app by chatting further with Bixbite AI.',
    color: '#0288d1',
  },
  {
    icon: <LayersIcon />,
    title: 'Full-stack code is generated',
    desc: 'Get production-ready frontend and backend code automatically.',
    color: '#2e7d32',
  },
  {
    icon: <RocketLaunchIcon />,
    title: 'Export or launch',
    desc: 'Export your code or launch your application directly from the platform.',
    color: '#c62828',
  },
];

/* What Our Users Say - commented out for later use
const testimonials = [
  {
    name: 'Shiva Nathan',
    avatar: '/men/32.jpg',
    quote: 'Bixbite AI turned my startup idea into a working app in hours. No coding, just conversation!',
    role: 'Founder',
  },
  {
    name: 'Raunak Goyal',
    avatar: '/women/44.jpg',
    quote: 'I built a custom CRM for my business by chatting with Bixbite AI. It\'s a game changer!',
    role: 'Small Business Owner',
  },
  {
    name: 'Bharath Jain',
    avatar: '/men/65.jpg',
    quote: 'The AI-generated code was clean and production-ready. Highly recommended for non-coders!',
    role: 'Product Manager',
  },
];
*/

const useTypewriter = (text, speed = 28) => {
  const [displayed, setDisplayed] = useState('');
  const indexRef = useRef(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    setDisplayed('');
    indexRef.current = 0;

    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      indexRef.current += 1;
      if (indexRef.current <= text.length) {
        setDisplayed(text.slice(0, indexRef.current));
      } else {
        clearInterval(intervalRef.current);
      }
    }, speed);

    return () => clearInterval(intervalRef.current);
  }, [text, speed]);

  return displayed;
};

const HomePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [hoveredStep, setHoveredStep] = useState(null);
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [orbitRotation, setOrbitRotation] = useState(0);
  const [useINR, setUseINR] = useState(detectCurrencySync()); // Initial sync detection

  // Detect currency based on IP location
  useEffect(() => {
    detectCurrency().then((isINR) => {
      setUseINR(isINR);
    });
  }, []);

  const registrationText = useINR 
    ? 'Build Your App for Just ₹400 – Start Creating Now!'
    : 'Build Your App for Just $5 – Start Creating Now!';
  const typewriterText = useTypewriter(registrationText, 28);

  // Rotate orbiting icons by 120 degrees on hover
  const handleOrbitHover = () => {
    setOrbitRotation((prev) => prev + 120);
  };

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic',
      disable: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    });
  }, []);

  const handleStartBuilding = (e) => {
    e.preventDefault();
    window.open('https://bixbite.ai/', '_blank', 'noopener,noreferrer');
  };

  const handleWatchDemo = (e) => {
    e.preventDefault();
    setVideoModalOpen(true);
  };

  const handleCloseVideoModal = () => {
    setVideoModalOpen(false);
  };

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh', width: '100%', overflowX: 'hidden' }}>

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 'calc(100vh - 70px)', md: '90vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, md: 6 },
          py: { xs: 6, md: 10 },
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #e0f7f4 0%, #b2dfdb 25%, #80cbc4 50%, #4db6ac 75%, #26a69a 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradient-shift 15s ease infinite',
        }}
        id="hero"
      >
        {/* Animated Background Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: { xs: 120, md: 200 },
            height: { xs: 120, md: 200 },
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(0, 121, 107, 0.15) 0%, rgba(0, 77, 64, 0.1) 100%)',
            filter: 'blur(40px)',
            animation: 'float 8s ease-in-out infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '15%',
            right: '10%',
            width: { xs: 150, md: 280 },
            height: { xs: 150, md: 280 },
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(0, 77, 64, 0.12) 0%, rgba(0, 121, 107, 0.08) 100%)',
            filter: 'blur(50px)',
            animation: 'floatReverse 10s ease-in-out infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '40%',
            right: '25%',
            width: { xs: 60, md: 100 },
            height: { xs: 60, md: 100 },
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.3)',
            filter: 'blur(20px)',
            animation: 'float 6s ease-in-out infinite 1s',
          }}
        />

        <Grid container spacing={4} alignItems="center" justifyContent="center" sx={{ position: 'relative', zIndex: 1, maxWidth: 1400 }}>
          <Grid item xs={12} md={6}>
            <Fade in timeout={800}>
              <Stack spacing={3} alignItems={{ xs: 'center', md: 'flex-start' }}>
                <Chip
                  icon={<TrendingUpIcon sx={{ color: 'white !important' }} />}
                  label="AI-Powered Development"
                  sx={{
                    background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                    color: 'white',
                    fontWeight: 600,
                    px: 1,
                    py: 2.5,
                    borderRadius: '50px',
                    boxShadow: '0 4px 20px -4px rgba(0, 121, 107, 0.4)',
                    '& .MuiChip-icon': { color: 'white' },
                  }}
                  data-aos="fade-down"
                />
                
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem', lg: '4rem' },
                    lineHeight: 1.1,
                    textAlign: { xs: 'center', md: 'left' },
                    color: '#004d40',
                  }}
                  data-aos="fade-right"
                  data-aos-delay="100"
                >
                  Build Software with{' '}
                  <Box
                    component="span"
                    sx={{
                      background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    Bixbite AI
                  </Box>
                </Typography>

                <Typography
                  variant="h5"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 400,
                    maxWidth: 540,
                    textAlign: { xs: 'center', md: 'left' },
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                    lineHeight: 1.6,
                  }}
                  data-aos="fade-right"
                  data-aos-delay="200"
                >
                  Effortless software creation. Just chat with{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                    Bixbite AI
                  </Box>{' '}
                  and bring your ideas to life.
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    width: '100%',
                    maxWidth: { xs: '100%', sm: 420 },
                    mt: 2,
                  }}
                  data-aos="fade-up"
                  data-aos-delay="300"
                >
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<PlayCircleOutlineIcon />}
                    sx={{
                      flex: 1,
                      py: 1.8,
                      fontWeight: 700,
                      fontSize: '1.05rem',
                      borderRadius: '14px',
                      borderWidth: 2,
                      borderColor: 'rgba(0, 77, 64, 0.3)',
                      color: '#004d40',
                      bgcolor: 'rgba(255, 255, 255, 0.7)',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        borderWidth: 2,
                        borderColor: '#004d40',
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 10px 30px -10px rgba(0, 77, 64, 0.3)',
                      },
                    }}
                    onClick={handleWatchDemo}
                    aria-label="Watch Demo"
                  >
                    Watch Demo
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIosIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      flex: 1,
                      py: 1.8,
                      fontWeight: 700,
                      fontSize: '1.05rem',
                      borderRadius: '14px',
                      background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                      boxShadow: '0 8px 30px -8px rgba(0, 121, 107, 0.5)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 14px 40px -10px rgba(0, 121, 107, 0.6)',
                      },
                    }}
                    onClick={handleStartBuilding}
                    aria-label="Start Building with Bixbite.ai"
                  >
                    Start Building
                  </Button>
                </Box>

                {/* Registration Incentive */}
                <Box
                  sx={{
                    mt: 3,
                  }}
                  data-aos="fade-up"
                  data-aos-delay="400"
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: '#004d40',
                      fontWeight: 700,
                      fontSize: { xs: '1rem', md: '1.15rem' },
                      textAlign: { xs: 'center', md: 'left' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      textShadow: '0 1px 2px rgba(255,255,255,0.5)',
                    }}
                  >
                    <AutoAwesomeIcon sx={{ color: '#004d40', fontSize: 22 }} />
                    {typewriterText || registrationText}
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        width: 2,
                        height: 20,
                        bgcolor: '#004d40',
                        ml: 0.5,
                        animation: 'pulse 1s infinite',
                      }}
                    />
                  </Typography>
                </Box>
              </Stack>
            </Fade>
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
            <Box
              sx={{
                position: 'relative',
                width: { md: 400, lg: 450 },
                height: { md: 400, lg: 450 },
              }}
              data-aos="zoom-in"
              data-aos-delay="300"
            >
              {/* Decorative Rings */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: -20,
                  borderRadius: '50%',
                  border: '2px dashed rgba(0, 121, 107, 0.2)',
                  animation: 'rotate 30s linear infinite',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: -40,
                  borderRadius: '50%',
                  border: '1px dashed rgba(0, 77, 64, 0.15)',
                  animation: 'rotate 45s linear infinite reverse',
                }}
              />
              
              {/* Main Image Container */}
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 30px 60px -20px rgba(0, 77, 64, 0.3)',
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '3px solid rgba(255, 255, 255, 0.5)',
                  animation: 'float 6s ease-in-out infinite',
                }}
              >
                <Box
                  component="img"
                  src="/home_page_right_side.png"
                  alt="Bixbite AI Illustration"
                  sx={{
                    width: '95%',
                    height: '95%',
                    objectFit: 'cover',
                    borderRadius: '50%',
                  }}
                />
              </Box>

              {/* Orbiting Icons - Rotate around the circle on hover */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: 0,
                  height: 0,
                  transform: `translate(-50%, -50%) rotate(${orbitRotation}deg)`,
                  transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                  pointerEvents: 'none',
                }}
              >
                {/* Icon 1 - Code (Top - 0deg / 270deg from right) */}
                <Box
                  onMouseEnter={handleOrbitHover}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    width: 44,
                    height: 44,
                    marginLeft: '-22px',
                    marginTop: '-22px',
                    transform: `rotate(-90deg) translateX(200px) rotate(${90 - orbitRotation}deg)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '12px',
                    bgcolor: 'white',
                    boxShadow: '0 6px 20px -6px rgba(0, 77, 64, 0.4)',
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease, background-color 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 10px 28px -6px rgba(0, 77, 64, 0.55)',
                      bgcolor: '#00796b',
                      '& svg': {
                        color: 'white',
                      },
                    },
                  }}
                >
                  <CodeIcon sx={{ fontSize: 24, color: '#00796b', transition: 'color 0.3s ease' }} />
                </Box>

                {/* Icon 2 - Chat (Bottom Left - 120deg from top) */}
                <Box
                  onMouseEnter={handleOrbitHover}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    width: 44,
                    height: 44,
                    marginLeft: '-22px',
                    marginTop: '-22px',
                    transform: `rotate(150deg) translateX(200px) rotate(${-150 - orbitRotation}deg)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '12px',
                    bgcolor: 'white',
                    boxShadow: '0 6px 20px -6px rgba(0, 77, 64, 0.4)',
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease, background-color 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 10px 28px -6px rgba(0, 77, 64, 0.55)',
                      bgcolor: '#004d40',
                      '& svg': {
                        color: 'white',
                      },
                    },
                  }}
                >
                  <ChatIcon sx={{ fontSize: 24, color: '#004d40', transition: 'color 0.3s ease' }} />
                </Box>

                {/* Icon 3 - Rocket (Bottom Right - 240deg from top) */}
                <Box
                  onMouseEnter={handleOrbitHover}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    width: 44,
                    height: 44,
                    marginLeft: '-22px',
                    marginTop: '-22px',
                    transform: `rotate(30deg) translateX(200px) rotate(${-30 - orbitRotation}deg)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '12px',
                    bgcolor: 'white',
                    boxShadow: '0 6px 20px -6px rgba(0, 77, 64, 0.4)',
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease, background-color 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 10px 28px -6px rgba(0, 77, 64, 0.55)',
                      bgcolor: '#00897b',
                      '& svg': {
                        color: 'white',
                      },
                    },
                  }}
                >
                  <RocketLaunchIcon sx={{ fontSize: 24, color: '#00897b', transition: 'color 0.3s ease' }} />
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Features Section */}
      <Box
        id="features"
        sx={{
          py: { xs: 8, md: 12 },
          px: { xs: 2, md: 6 },
          bgcolor: 'white',
          position: 'relative',
        }}
      >
        {/* Section Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'radial-gradient(rgba(0, 121, 107, 0.03) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
            pointerEvents: 'none',
          }}
        />

        <Box sx={{ maxWidth: 1200, mx: 'auto', position: 'relative' }}>
          <Typography
            variant="h3"
            align="center"
            sx={{ 
              fontWeight: 800, 
              mb: 2,
              background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
            data-aos="fade-up"
          >
            Why Bixbite AI?
          </Typography>
          <Typography
            align="center"
            sx={{
              color: theme.palette.text.secondary,
              mb: 6,
              maxWidth: 600,
              mx: 'auto',
              fontSize: '1.1rem',
            }}
            data-aos="fade-up"
            data-aos-delay="100"
          >
            Experience the future of software development with our powerful AI capabilities
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {features.map((feature, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: '24px',
                    textAlign: 'center',
                    height: '100%',
                    border: '1px solid rgba(0, 121, 107, 0.1)',
                    background: 'linear-gradient(180deg, #ffffff 0%, #f8fffe 100%)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: `linear-gradient(90deg, ${feature.color}, ${feature.color}88)`,
                      transform: 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.4s ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 20px 50px -15px rgba(0, 77, 64, 0.2)',
                      borderColor: 'rgba(0, 121, 107, 0.2)',
                    },
                    '&:hover::before': {
                      transform: 'scaleX(1)',
                    },
                  }}
                  data-aos="fade-up"
                  data-aos-delay={100 * idx}
                >
                  <Box
                    sx={{
                      mb: 3,
                      display: 'inline-flex',
                      p: 2,
                      borderRadius: '16px',
                      background: `linear-gradient(135deg, ${feature.color}15 0%, ${feature.color}08 100%)`,
                      color: feature.color,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#1a2f2c' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                    {feature.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Our Products Section */}
      <Box
        id="products"
        sx={{
          py: { xs: 8, md: 12 },
          px: { xs: 2, md: 6 },
          background: 'linear-gradient(180deg, #f8fffe 0%, #e0f7f4 100%)',
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontWeight: 800,
              mb: 2,
              color: '#004d40',
            }}
            data-aos="fade-up"
          >
            Our Products
          </Typography>
          <Typography
            align="center"
            sx={{
              color: theme.palette.text.secondary,
              mb: 6,
              fontSize: '1.1rem',
              maxWidth: 700,
              mx: 'auto',
            }}
            data-aos="fade-up"
            data-aos-delay="100"
          >
            Build any of these applications in minutes with Bixbite AI. Just describe your idea, and our platform generates production-ready code for you—no coding required!
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {products.map((product, idx) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={product.title}
                sx={{ display: 'flex' }}
              >
                <Paper
                  elevation={0}
                  onMouseEnter={() => setHoveredProduct(idx)}
                  onMouseLeave={() => setHoveredProduct(null)}
                  onClick={() => product.title.includes('LoCoPilot') && navigate('/locopilot')}
                  sx={{
                    p: 3.5,
                    borderRadius: '20px',
                    textAlign: 'center',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    border: '1px solid rgba(0, 121, 107, 0.1)',
                    bgcolor: 'white',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      boxShadow: '0 25px 50px -15px rgba(0, 77, 64, 0.25)',
                    },
                  }}
                  data-aos="fade-up"
                  data-aos-delay={50 * idx}
                >
                  <Box
                    sx={{
                      mb: 2.5,
                      width: 70,
                      height: 70,
                      borderRadius: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: product.gradient,
                      color: 'white',
                      boxShadow: hoveredProduct === idx 
                        ? '0 12px 30px -8px rgba(0, 77, 64, 0.4)' 
                        : '0 8px 20px -8px rgba(0, 77, 64, 0.3)',
                      transition: 'all 0.3s ease',
                      transform: hoveredProduct === idx ? 'scale(1.1) rotate(5deg)' : 'scale(1)',
                      '& svg': { fontSize: 32 },
                    }}
                  >
                    {product.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: '#1a2f2c' }}>
                    {product.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                    {product.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Check Pricing Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }} data-aos="fade-up">
            <Button
              component={RouterLink}
              to="/bixbite_pricing"
              variant="contained"
              size="large"
              sx={{
                px: 6,
                py: 2,
                fontWeight: 700,
                fontSize: '1.15rem',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                boxShadow: '0 10px 35px -10px rgba(0, 121, 107, 0.5)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 16px 45px -12px rgba(0, 121, 107, 0.6)',
                },
              }}
              aria-label="Check Pricing"
            >
              Check Pricing
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Interactive Demo Section */}
      <Box
        id="demo"
        sx={{
          py: { xs: 8, md: 12 },
          px: { xs: 2, md: 6 },
          bgcolor: 'white',
          position: 'relative',
        }}
      >
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Typography
            variant="h3"
            align="center"
            sx={{ 
              fontWeight: 800, 
              mb: 2,
              color: '#004d40',
            }}
            data-aos="fade-up"
          >
            Try Bixbite AI in Action
          </Typography>
          <Typography
            align="center"
            sx={{
              color: theme.palette.text.secondary,
              mb: 6,
              fontSize: '1.1rem',
            }}
            data-aos="fade-up"
            data-aos-delay="100"
          >
            Experience our AI assistant firsthand
          </Typography>

          <Box
            sx={{
              borderRadius: '24px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px -20px rgba(0, 77, 64, 0.25)',
              border: '1px solid rgba(0, 121, 107, 0.1)',
            }}
            data-aos="zoom-in"
            data-aos-delay="200"
          >
            <ChatDemo />
          </Box>
        </Box>
      </Box>

      {/* How It Works Section */}
      <Box
        id="how-it-works"
        sx={{
          py: { xs: 8, md: 12 },
          px: { xs: 2, md: 6 },
          background: 'linear-gradient(180deg, #f8fffe 0%, #ffffff 100%)',
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          <Typography
            variant="h3"
            align="center"
            sx={{ 
              fontWeight: 800, 
              mb: 2,
              background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
            data-aos="fade-up"
          >
            How It Works
          </Typography>
          <Typography
            align="center"
            sx={{
              color: theme.palette.text.secondary,
              mb: 6,
              maxWidth: 600,
              mx: 'auto',
              fontSize: '1.1rem',
            }}
            data-aos="fade-up"
            data-aos-delay="100"
          >
            Six simple steps from idea to launch
          </Typography>

          <Grid
            container
            spacing={3}
            justifyContent="center"
            alignItems="stretch"
          >
            {howItWorks.map((step, idx) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={2}
                key={idx}
                sx={{ display: 'flex' }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: '20px',
                    textAlign: 'center',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    border: '1px solid rgba(0, 121, 107, 0.1)',
                    bgcolor: 'white',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 50px -15px rgba(0, 77, 64, 0.2)',
                    },
                  }}
                  onMouseEnter={() => setHoveredStep(idx)}
                  onMouseLeave={() => setHoveredStep(null)}
                  data-aos="fade-up"
                  data-aos-delay={80 * idx}
                >
                  {/* Step Number Badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      boxShadow: '0 4px 12px -2px rgba(0, 77, 64, 0.4)',
                    }}
                  >
                    {idx + 1}
                  </Box>

                  <Box
                    sx={{
                      mb: 2,
                      mt: 1,
                      width: 60,
                      height: 60,
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `${step.color}15`,
                      color: step.color,
                      transition: 'all 0.3s ease',
                      transform: hoveredStep === idx ? 'scale(1.1)' : 'scale(1)',
                      '& svg': { fontSize: 30 },
                    }}
                  >
                    {step.icon}
                  </Box>

                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: '#1a2f2c',
                      fontSize: '0.95rem',
                      lineHeight: 1.3,
                      minHeight: 48,
                    }}
                  >
                    {step.title}
                  </Typography>

                  <Zoom
                    in={hoveredStep === idx}
                    timeout={200}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.85rem',
                        lineHeight: 1.5,
                        opacity: hoveredStep === idx ? 1 : 0,
                        transition: 'opacity 0.3s ease',
                      }}
                    >
                      {step.desc}
                    </Typography>
                  </Zoom>

                  {/* Mobile description always visible */}
                  <Typography
                    variant="body2"
                    sx={{
                      display: { xs: 'block', md: 'none' },
                      color: theme.palette.text.secondary,
                      fontSize: '0.85rem',
                      lineHeight: 1.5,
                      mt: 1,
                    }}
                  >
                    {step.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Testimonials Section - What Our Users Say - commented out for later use
      <Box
        id="testimonials"
        sx={{
          py: { xs: 8, md: 12 },
          px: { xs: 2, md: 6 },
          bgcolor: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 600,
            height: 600,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0, 121, 107, 0.03) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <Box sx={{ maxWidth: 1200, mx: 'auto', position: 'relative' }}>
          <Typography
            variant="h3"
            align="center"
            sx={{ 
              fontWeight: 800, 
              mb: 2,
              color: '#004d40',
            }}
            data-aos="fade-up"
          >
            What Our Users Say
          </Typography>
          <Typography
            align="center"
            sx={{
              color: theme.palette.text.secondary,
              mb: 6,
              fontSize: '1.1rem',
            }}
            data-aos="fade-up"
            data-aos-delay="100"
          >
            Trusted by entrepreneurs and businesses worldwide
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {testimonials.map((t, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: '24px',
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    border: '1px solid rgba(0, 121, 107, 0.1)',
                    bgcolor: 'linear-gradient(180deg, #ffffff 0%, #f8fffe 100%)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 50px -15px rgba(0, 77, 64, 0.2)',
                    },
                  }}
                  data-aos="fade-up"
                  data-aos-delay={100 * idx}
                >
                  <FormatQuoteIcon 
                    sx={{ 
                      fontSize: 48, 
                      color: theme.palette.primary.light, 
                      mb: 2,
                      opacity: 0.5,
                    }} 
                  />
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontStyle: 'italic', 
                      mb: 3,
                      color: theme.palette.text.secondary,
                      lineHeight: 1.7,
                      fontSize: '1.05rem',
                    }}
                  >
                    "{t.quote}"
                  </Typography>
                  <Avatar 
                    src={t.avatar} 
                    alt={t.name} 
                    sx={{ 
                      width: 64, 
                      height: 64, 
                      mb: 2,
                      border: '3px solid',
                      borderColor: theme.palette.primary.light,
                      boxShadow: '0 8px 24px -8px rgba(0, 77, 64, 0.3)',
                    }} 
                  />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a2f2c' }}>
                    {t.name}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: theme.palette.primary.main,
                      fontWeight: 500,
                    }}
                  >
                    {t.role}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
      */}

      {/* About Us Section */}
      <Box
        id="about"
        sx={{
          py: { xs: 8, md: 12 },
          px: { xs: 2, md: 6 },
          background: 'linear-gradient(180deg, #f8fffe 0%, #e0f7f4 100%)',
        }}
      >
        <AboutUs />
      </Box>

      {/* Contact Section */}
      <Box
        id="contact"
        sx={{
          py: { xs: 8, md: 12 },
          px: { xs: 2, md: 6 },
          bgcolor: 'white',
        }}
      >
        <Contact />
      </Box>

      <Footer />

      {/* Video Modal */}
      <Dialog
        open={videoModalOpen}
        onClose={handleCloseVideoModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
          },
        }}
        BackdropProps={{
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
          },
        }}
      >
        <Box sx={{ position: 'relative' }}>
          {/* Close Button */}
          <IconButton
            onClick={handleCloseVideoModal}
            sx={{
              position: 'absolute',
              top: -50,
              right: 0,
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.1)',
              },
            }}
            aria-label="Close video"
          >
            <CloseIcon sx={{ fontSize: 28 }} />
          </IconButton>

          {/* Video Container */}
          <Box
            sx={{
              position: 'relative',
              paddingTop: '56.25%', // 16:9 Aspect Ratio
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 25px 80px -20px rgba(0, 0, 0, 0.5)',
            }}
          >
            <iframe
              src={videoModalOpen ? "https://www.youtube.com/embed/tKFVSbfj3AM?autoplay=1&rel=0" : ""}
              title="Bixbite AI Demo"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default HomePage;
